import React, { useCallback, useRef, useState } from 'react';
import { AbsoluteSmallSpinner } from '@components/core-ui/spinners/Spinner';
import { useUploadContext } from '../UploadProvider';
import { softValidate } from './helpers/softValidate';
import type { TProps } from './types/TProps';

export const DropZone: React.FC<TProps> = ({
  accept = 'audio/x-m4a, audio/mpeg, audio/wav, audio/x-wav, audio/*, video/x-msvideo, video/trm, .trm, .mp4',
  multiple = true,
  onFiles,
  onBeforeAddFiles,
  maxSizeBytes,
  minSizeBytes,
  className,
}) => {
  const { addFiles } = useUploadContext();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isOver, setOver] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [preparingFileCount, setPreparingFileCount] = useState(0);

  const handleFiles = useCallback(
    async (list: FileList | null) => {
      if (!list || isPreparing) {
        return;
      }
      const files = Array.from(list);
      const ok = softValidate(files, accept, minSizeBytes, maxSizeBytes);
      if (!ok.length) {
        return;
      }
      setPreparingFileCount(ok.length);
      setIsPreparing(true);
      try {
        if (onBeforeAddFiles) {
          try {
            await onBeforeAddFiles();
          } catch {
            return;
          }
        }
        onFiles?.(ok);
        await addFiles(ok);
      } finally {
        setPreparingFileCount(0);
        setIsPreparing(false);
      }
    },
    [addFiles, onFiles, onBeforeAddFiles, accept, minSizeBytes, maxSizeBytes, isPreparing],
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setOver(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  const onBrowse = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [handleFiles],
  );

  return (
    <div
      className={[
        'cursor-pointer rounded-[5px] border border-dashed border-[var(--color-accent)] hover:border-[var(--color-text-muted)]',
        'p-[15px] text-center relative overflow-hidden select-none font-[600] text-[12px] leading-[18px]',
        isOver
          ? 'ring-2 ring-[color:var(--color-accent)]/50 bg-[color:var(--color-surface-muted)] text-[color:var(--color-link)]'
          : 'text-[color:var(--color-text-muted)]',
        isPreparing ? 'pointer-events-none' : '',
        className || '',
      ].join(' ')}
      onDragOver={(e) => {
        e.preventDefault();
        if (!isPreparing) {
          setOver(true);
        }
      }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      onClick={() => {
        if (!isPreparing) {
          inputRef.current?.click();
        }
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !isPreparing) {
          inputRef.current?.click();
        }
      }}
    >
      <input
        ref={inputRef}
        type="file"
        className="invisible absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
        multiple={multiple}
        accept={accept}
        onChange={onBrowse}
        disabled={isPreparing}
      />
      <span className={isPreparing ? 'invisible' : undefined}>
        Drag and drop or click <span className="underline text-[color:var(--color-link)]">here</span> to upload your
        files
      </span>
      {isPreparing ? (
        <div className="absolute inset-0 flex items-center justify-center gap-2 text-[color:var(--color-text-muted)]">
          <span className="relative block h-5 w-5">
            <AbsoluteSmallSpinner />
          </span>
          <span>
            Preparing {preparingFileCount} {preparingFileCount === 1 ? 'file' : 'files'}...
          </span>
        </div>
      ) : null}
    </div>
  );
};
